import { useEffect, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  unlinkClientAccount,
  getLinkedAccounts,
  selectClientContactDetails,
  selectLoadingClientContactDetails,
} from "./cards/ClientContactDetails/components/LinkedAccountsSlice";
import { useDispatch, useSelector } from "react-redux";
import { ClientContactDetailsModel } from "services/api/cplapi/CPLAPI";
import { createColumnHelper } from "@tanstack/react-table";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { useNavigate } from "react-router-dom";
import {
  setClientAccountNumber,
  setClientID,
  setClientPropertyDetails,
} from "./cards/ClientContactDetails/components/ContactDetailsSlice";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { selectUserProfile } from "./cards/components/UserProfileSlice";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/pro-solid-svg-icons";

const LinkedAccounts: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const clientContactDetails = useSelector(selectClientContactDetails);
  const loading = useSelector(selectLoadingClientContactDetails);
  const clientPortalUserId = useSelector(selectUserProfile)?.clientPortalUserID ?? "";

  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
  const [selectedClientID, setSelectedClientID] = useState<string>("");

  const columnHelper = createColumnHelper<ClientContactDetailsModel>();
  const columnDefinition = [
    columnHelper.accessor((row) => row.property, {
      id: "Property Address",
      cell: (info) => info.getValue(),
      header: () => <>Property Address</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.billingAddress, {
      id: "Billing Address",
      cell: (info) => {
        return info.getValue() != "" ? info.getValue() : info.row.original.property;
      },
      header: () => <>Billing Address</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.emailAddress, {
      id: "Default Email",
      cell: (info) => info.getValue(),
      header: () => <>Default Email</>,
      enableColumnFilter: false,
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <div className="row g-2 min-w-60px min-w-md-275px">
          <div className="col-6">
            <div
              data-test-id={`linked-accounts-table__edit-details-button-${info.row.original.clientID}`}
              className="cpl-btn btn-sm cpl-btn-outline d-flex justify-content-center align-items-center w-100 "
              onClick={() => {
                navigateToContactDetails(info.row.original);
              }}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              aria-label="Edit Details"
              title="Edit Details"
            >
              <FontAwesomeIcon icon={faPen} className="d-md-none mr-2" />
              <span className="text-truncate d-none d-md-inline">Edit Details</span>
            </div>
          </div>
          <div className="col-6">
            <div
              data-test-id={`linked-accounts-table__remove-account-button-${info.row.original.clientID}`}
              className="cpl-btn btn-sm cpl-btn-outline d-flex justify-content-center align-items-center w-100 "
              onClick={() => showDeleteModal(info.row.original)}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              aria-label="Remove Account"
              title="Remove Account"
            >
              <FontAwesomeIcon icon={faTrash} className="d-md-none" />
              <span className="text-truncate d-none d-md-inline">Remove Account</span>
            </div>
          </div>
        </div>
      ),
      enableColumnFilter: false,
      size: 300,
    }),
  ];

  useEffect(() => {
    thunkDispatch(getLinkedAccounts());
  }, [thunkDispatch]);

  const navigateToContactDetails = (contactDetailsRow: ClientContactDetailsModel) => {
    dispatch(setClientID(contactDetailsRow.clientID));
    dispatch(setClientAccountNumber(contactDetailsRow.accountNumber));
    dispatch(setClientPropertyDetails(contactDetailsRow.property));
    const path = `/contactdetails/${contactDetailsRow.accountNumber}`;
    navigate(path);
  };

  const showDeleteModal = (contactDetailsRow: ClientContactDetailsModel) => {
    if (contactDetailsRow.clientID) {
      setSelectedClientID(contactDetailsRow.clientID);
    }
    setShowDeleteAlert(true);
  };

  const handleCloseDeleteAlert = async (answer: boolean) => {
    setShowDeleteAlert(false);
    if (answer) {
      await thunkDispatch(unlinkClientAccount({ clientPortalUserID: clientPortalUserId, clientID: selectedClientID }));
      await thunkDispatch(getLinkedAccounts());
    }
  };

  return (
    <div className="card">
      <ActivityIndicator isBusy={loading}>
        <div className="card-body px-0">
          <CPLTable
            data={clientContactDetails}
            columns={columnDefinition}
            rowMobileClickAction={(row) => navigateToContactDetails(row)}
            classNames="cpl-table-nowrap-below-desktop"
          />
          <YesNoAlert
            showAlert={showDeleteAlert}
            alertTitle="Delete Confirmation"
            alertText="Please confirm that you would like to remove this account?"
            style={Style.Warning}
            callBack={handleCloseDeleteAlert}
          />
        </div>
      </ActivityIndicator>
    </div>
  );
};

export { LinkedAccounts };
